const {
  getElements,
  formatNumberWithCommas,
  parseFormattedNumber,
  currencyExchange,
} = window.BAO.Utils
class PriceRange extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['min', 'max']
  }

  constructor () {
    super()

    switch (this.dataset.type) {
      case 'search':
        this.eventBus = window.theme.searchResults.eventBus
        this.state = {
          root: window.theme.searchResults?.stateLayer?.state || {
            products: [],
          },
          observer: null,
        }

        this.type = 'klevu'
        break
      case 'autocomplete':
        this.eventBus = window.theme.autocompleteResults.eventBus
        this.state = {
          root: window.theme.autocompleteResults?.stateLayer?.state || {
            products: [],
          },
          observer: null,
        }

        this.type = 'klevu'
        break
      case 'shopify':
        this.type = 'shopify'
        break
      default:
        // Default is collection
        this.eventBus = window.theme.category.eventBus
        this.state = {
          root: window.theme.category?.stateLayer?.state || { products: [] },
          observer: null,
        }

        this.type = 'klevu'
    }

    this.updateMinMaxConstraints()
  }

  connectedCallback () {
    super.connectedCallback()
    this.formatInitialValues()
  }

  setupListeners () {
    super.setupListeners()

    getElements('input', { context: this }).forEach(el => {
      this.listeners.add(el, 'change', this.onRangeChange.bind(this))
      this.listeners.add(el, 'blur', this.onInputBlur.bind(this))
      this.listeners.add(el, 'focus', this.onInputFocus.bind(this))
      this.listeners.add(el, 'input', this.onInput.bind(this))
    })

    if (this.type === 'klevu') {
      this.eventBus.subscribe('state:resetPriceRange', () => {
        this.minInput.value = formatNumberWithCommas(
          this.minInput.dataset.rangeMin,
        )
        this.maxInput.removeAttribute('value')
      })
    }
  }

  /**
   * Handle input changes from range slider
   * Formats values when changed programmatically
   * @param {InputEvent} e
   * @returns {void}
   */
  onInput (e) {
    const input = e.target

    // Only format if changed by slider (input not focused)
    if (document.activeElement !== input && input.value !== '') {
      const numericValue = parseFormattedNumber(input.value)
      if (!isNaN(numericValue)) {
        input.value = formatNumberWithCommas(numericValue)
      }
    }
  }

  /**
   * Format and validate value when input loses focus
   * @param {FocusEvent} e
   * @returns {void}
   */
  onInputBlur (e) {
    const input = e.target
    let value = parseFormattedNumber(input.value)
    const min = Number(input.dataset.rangeMin)
    const max = Number(input.dataset.rangeMax)
    const isMaxInput = input.dataset?.priceRangeEl === 'max'

    // Handle invalid input - set to appropriate boundary
    if (isNaN(value) || input.value === '') {
      value = isMaxInput ? max : min
    }

    // Clamp to min/max boundaries
    value = Math.max(min, Math.min(value, max))

    input.value = formatNumberWithCommas(value)
  }

  /**
   * Remove formatting when input gains focus for easier editing
   * @param {FocusEvent} e
   * @returns {void}
   */
  onInputFocus (e) {
    const input = e.target

    // Don't process empty or placeholder-only inputs
    if (!input.value || input.value.trim() === '') {
      return
    }

    const numericValue = parseFormattedNumber(input.value)
    // Only remove formatting if we have a valid non-zero number
    if (!isNaN(numericValue) && numericValue > 0) {
      input.value = numericValue.toString()
    }
  }

  /**
   * Handle range change events - validates inputs and publishes changes
   * @param {InputEvent} e
   * @returns {void}
   */
  onRangeChange (e) {
    // Validate both inputs to ensure no NaN values
    this.validateInput(this.minInput)
    this.validateInput(this.maxInput)

    this.updateMinMaxConstraints()

    const minValue = parseFormattedNumber(
      currencyExchange(this.minInput.value, '', true),
    )
    const minValueNotExchanged = parseFormattedNumber(this.minInput.value)

    const maxReachedInt = Number(this.maxInput.dataset.maxReachedInt)
    const maxReachedLimit = Number(this.maxInput.dataset.maxReachedLimit)
    let maxValueInt = parseFormattedNumber(this.maxInput.value)
    let maxVisual = parseFormattedNumber(maxValueInt)

    let maxValue = parseFormattedNumber(
      currencyExchange(this.maxInput.value, '', true),
    )

    if (maxValueInt >= maxReachedLimit) {
      maxValueInt = maxReachedInt
      maxVisual = `${parseFormattedNumber(maxReachedLimit)}+`
      maxValue = parseFormattedNumber(maxValueInt)
    }

    const maxValueNotExchanged = parseFormattedNumber(maxValueInt)

    this.closest('form')?.dispatchEvent(
      new Event('input', {
        bubbles: true,
        cancelable: true,
      }),
    )

    if (this.type === 'klevu') {
      this.eventBus.publish('price-range:change', {
        min: minValue,
        minValueNotExchanged,
        max: maxValue,
        maxValueNotExchanged,
        maxVisual,
      })
    }

    this.dispatchEvent(
      new CustomEvent('AFTER_PRICE_RANGE_CHANGE', {
        bubbles: true,
        detail: {
          min: minValue,
          max: maxValue,
        },
      }),
    )
  }

  /**
   * Update cross-constraints between min and max inputs
   * Ensures min can't exceed max and max can't go below min
   * @returns {void}
   */
  updateMinMaxConstraints () {
    const minValue = parseFormattedNumber(this.minInput.value)

    const maxReachedLimit = Number(this.maxInput.dataset.maxReachedLimit)
    let maxValueInt = parseFormattedNumber(this.maxInput.value)

    if (maxValueInt === maxReachedLimit) {
      maxValueInt = maxReachedLimit
    }

    const maxValue = parseFormattedNumber(maxValueInt)

    // Set max constraint on min input
    if (this.maxInput.value) {
      this.minInput.dataset.rangeMax = maxValue
    }

    // Set min constraint on max input
    if (this.minInput.value) {
      this.maxInput.dataset.rangeMin = minValue
    } else {
      this.maxInput.dataset.rangeMin = '0'
    }
  }

  /**
   * Format initial values with commas
   *
   * @returns {void}
   */
  formatInitialValues () {
    if (this.minInput.value && this.minInput.value !== '') {
      const minValue = parseFormattedNumber(this.minInput.value)
      if (!isNaN(minValue)) {
        this.minInput.value = formatNumberWithCommas(minValue)
      }
    }
    if (this.maxInput.value && this.maxInput.value !== '') {
      const maxValue = parseFormattedNumber(this.maxInput.value)
      if (!isNaN(maxValue)) {
        this.maxInput.value = formatNumberWithCommas(maxValue)
      }
    }
  }

  /**
   * Validate and correct input value to be within range
   * @param {HTMLInputElement} input - The input element to validate
   * @returns {void}
   */
  validateInput (input) {
    const value = parseFormattedNumber(input.value)
    const min = Number(input.dataset.rangeMin)

    const maxReachedLimit = Number(this.maxInput.dataset.maxReachedLimit)
    let maxValueInt = Number(input.dataset.rangeMax)

    if (maxValueInt === maxReachedLimit) {
      maxValueInt = Number(maxReachedLimit)
    }

    const max = maxValueInt

    const isMaxInput = input.dataset?.priceRangeEl === 'max'

    // Handle invalid input (NaN or empty)
    if (isNaN(value)) {
      const boundaryValue = isMaxInput ? max : min
      input.value = formatNumberWithCommas(boundaryValue)
      return
    }

    // Special case: max input with 0 should default to max
    if (isMaxInput && value === 0) {
      input.value = formatNumberWithCommas(max)
      return
    }

    // Clamp value within min/max boundaries
    if (value < min) {
      input.value = formatNumberWithCommas(min)
    } else if (value > max) {
      input.value = formatNumberWithCommas(max)
    }
  }

  /**
   * @returns {HTMLInputElement}
   */
  get minInput () {
    return this.els.min.element
  }

  /**
   * @returns {HTMLInputElement}
   */
  get maxInput () {
    return this.els.max.element
  }

  /**
   * @returns {HTMLInputElement}
   */
  get maxInputValue () {
    return this.els.max.element
  }
}

if (!customElements.get('price-range')) {
  customElements.define('price-range', PriceRange)
}
